import React from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { CheckCircle2, TrendingUp, Award, Zap, Users, Sparkles, ShieldCheck } from "lucide-react";

const startFlow = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function ListYourVenue() {
    useSEO({
        title: "List your venue | Venuely",
        description: "Reach thousands of people planning private events. List your venue on Venuely for free and start receiving enquiries.",
    });

    const benefits = [
        { icon: TrendingUp, title: "Get more enquiries", desc: "Reach thousands of people planning private events every month." },
        { icon: ShieldCheck, title: "Free to list", desc: "Basic listings are free forever. Upgrade only when you want more visibility." },
        { icon: Zap, title: "Fast setup", desc: "Get your listing live in under 15 minutes with our guided flow." },
        { icon: Award, title: "Verified badge", desc: "Build trust with verified status and unlock premium placement." },
    ];

    return (
        <div>
            <section className="relative overflow-hidden bg-primary text-primary-foreground">
                <div className="absolute inset-0 subtle-noise opacity-30" />
                <div className="container-app relative py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">For venue owners</span>
                        <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight">Get more private hire enquiries.</h1>
                        <p className="mt-4 text-lg text-primary-foreground/80 max-w-xl">List your venue on Venuely and reach people planning birthdays, corporate events, weddings, private dining and more — worldwide.</p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Button className="rounded-full h-12 px-8 bg-secondary hover:bg-secondary/90 text-secondary-foreground text-base" onClick={startFlow} data-testid="lyv-cta-primary">
                                Add your venue — it's free
                            </Button>
                            <Button asChild variant="outline" className="rounded-full h-12 px-8 bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 text-base">
                                <Link to="/how-it-works">See how it works</Link>
                            </Button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {benefits.map(({ icon: Icon, title, desc }) => (
                            <div key={title} className="rounded-2xl bg-primary-foreground/10 border border-primary-foreground/10 p-5">
                                <Icon className="h-6 w-6 text-accent" />
                                <p className="mt-3 font-display text-lg font-semibold">{title}</p>
                                <p className="text-sm text-primary-foreground/70 mt-1">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="container-app py-16 md:py-24">
                <h2 className="font-display text-3xl md:text-4xl font-semibold text-center">How it works</h2>
                <div className="grid md:grid-cols-3 gap-6 mt-10">
                    {[
                        { n: "01", t: "Create your account", d: "Sign in securely with Google — no passwords to remember." },
                        { n: "02", t: "Add your venue", d: "Guided form: details, photos, features, event types. Save as you go." },
                        { n: "03", t: "Get enquiries", d: "Approved listings appear across our SEO pages and search results." },
                    ].map((s) => (
                        <div key={s.n} className="rounded-2xl border border-border p-6 bg-card">
                            <span className="font-display text-4xl font-semibold text-secondary">{s.n}</span>
                            <h3 className="font-display text-xl mt-3 font-semibold">{s.t}</h3>
                            <p className="text-sm text-muted-foreground mt-2">{s.d}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-12 rounded-2xl bg-muted/40 border border-border p-6 md:p-10 text-center">
                    <h3 className="font-display text-2xl md:text-3xl font-semibold">Ready to list?</h3>
                    <p className="text-muted-foreground mt-2 max-w-md mx-auto">Sign in with Google to add your venue in minutes.</p>
                    <Button className="mt-5 rounded-full h-11 px-8 bg-primary hover:bg-primary/90 text-primary-foreground" onClick={startFlow} data-testid="lyv-cta-bottom">
                        Get started for free
                    </Button>
                </div>
            </section>
        </div>
    );
}
